import { ChangeDetectionStrategy, Component, booleanAttribute, computed, inject, input, output } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { HdCycleGateLineInfoRequest } from '../channel-list/hd-cycle-gate-keynote-line.component';
import { HdBodygraphCenter } from '../hd-bodygraph.models';
import { HdActivePaletteService } from '../hd-active-palette.service';

/** Eén planeetactivatie die het center tijdelijk mee definieert: glyph + poort-pil "30.5". */
export interface HdCycleCenterBlockCause {
  readonly glyph: string;
  readonly gate: number;
  readonly line: number;
  /** Tooltip: planeetnaam + poort.lijn. */
  readonly title: string;
  /** Pil-/glyphkleur; default transit-groen (cycle, moment); composite: per persoon. */
  readonly color?: string;
}

/** Aanvraag om de center-info te openen (zelfde shape als de chart-klik). */
export interface HdCycleCenterBlockInfoRequest {
  readonly center: HdBodygraphCenter;
  readonly origin: { x: number; y: number };
}

/**
 * "Niet zelf center thema"-blokje: een ongedefinieerd natal-center dat door de
 * cyclus/het moment/de partner definitie krijgt, als cheat-sheet-blokje
 * (center-shape in chartkleur + label + cyclus-specifiek thema). Optioneel
 * klikbaar (opent de center-info) en met een regel poort-pillen van de
 * activaties die het center tijdelijk definiëren ({@link causes}); een pil
 * opent de poortinfo, de rest van het blokje de center-info.
 *
 * Gedeeld door de keynote-secties (cycle/moment), de composite-secties en het
 * Just Now-paneel.
 */
@Component({
  selector: 'hd-cycle-conditioned-center-block',
  templateUrl: './hd-cycle-conditioned-center-block.component.html',
  styleUrl: './hd-cycle-conditioned-center-block.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdCycleConditionedCenterBlockComponent {
  private readonly centerColors = inject(HdActivePaletteService);

  readonly center = input.required<HdBodygraphCenter>();
  readonly label = input.required<string>();
  /** HTML met `<b>`-accenten (cyclus-specifiek niet-zelf thema uit de cycles-scope). */
  readonly text = input.required<string>();
  readonly causes = input<readonly HdCycleCenterBlockCause[]>([]);
  /** Klik opent de center-info ({@link centerInfoRequested}). */
  readonly clickable = input(false, { transform: booleanAttribute });

  readonly centerInfoRequested = output<HdCycleCenterBlockInfoRequest>();
  /** Klik op een poort-pil → poortinfo (zoals de poort-chips in de kanaal-items). */
  readonly gateInfoRequested = output<HdCycleGateLineInfoRequest>();

  /** Center-shape in de paletkleur (defined-stijl), zoals op de cheat-sheet-pagina. */
  protected readonly shape = computed<SafeHtml>(() =>
    this.centerColors.iconHtml(this.center(), 'display:block;width:auto;height:100%;overflow:visible;margin:0 auto'),
  );

  protected onClick(event: MouseEvent): void {
    if (!this.clickable()) {
      return;
    }
    this.centerInfoRequested.emit({ center: this.center(), origin: { x: event.clientX, y: event.clientY } });
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (!this.clickable() || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }
    event.preventDefault();
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.centerInfoRequested.emit({ center: this.center(), origin: { x: rect.right, y: rect.top } });
  }

  protected onCauseClick(cause: HdCycleCenterBlockCause, event: MouseEvent): void {
    // De pil opent de poort, niet (ook) het center.
    event.stopPropagation();
    this.gateInfoRequested.emit({ gate: cause.gate, origin: { x: event.clientX, y: event.clientY } });
  }
}
